/**
 * Route config. Same paths and components as before; no behavior change.
 * Pages imported from feature entry points where available.
 * Each route is wrapped in ErrorBoundary so a crash on one page
 * does not take down the entire app.
 */
import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { appConfig } from '../config/app-config';
import AccountPage from '../features/auth/account-page';
import PingCallback from '../features/auth/ping-callback';
import CartPage from '../features/cart/components/cartPage/cartPage';
import CheckoutPage from '../features/checkout/checkout-page';
import HomePage from '../features/home/components/HomePage';
import EventPrivacyPolicyPage from '../features/legal/EventPrivacyPolicyPage';
import InvoicePage from '../features/order/components/InvoicePage';
import OrderPage from '../features/order/components/OrderPage';
import { SharedBasketPage } from '../features/shared-basket';
import StoreLayout from '../features/store/components/StoreLayout';
import NotFound from '../shared/components/NotFound';
import ProtectedRoute from '../shared/components/ProtectedRoute';

import ErrorBoundary from './layout/ErrorBoundary';

/** Wrap a page element in an ErrorBoundary */
const safe = (element: React.ReactElement): React.ReactElement => <ErrorBoundary>{element}</ErrorBoundary>;

export default function AppRoutes() {
  return (
    <Routes>
      <Route path="/ping-callback" element={safe(<PingCallback />)} />
      <Route path="/shared-basket/:shareToken" element={safe(<SharedBasketPage />)} />

      <Route path="/:storeKey" element={<StoreLayout />}>
        {/* Store-Level Routes */}
        <Route path="cart" element={safe(<CartPage />)} />
        <Route path="checkout" element={
          <ProtectedRoute>
            {safe(<CheckoutPage />)}
          </ProtectedRoute>
        } />
        <Route path="invoice-confirmation" element={safe(<InvoicePage />)} />

        <Route path="account" element={safe(<AccountPage />)} />
        <Route path="order" element={
          <ProtectedRoute>
            {safe(<OrderPage />)}
          </ProtectedRoute>
        } />

        {/* Event-Level Routes */}
        <Route path=":eventKey">
          <Route index element={safe(<HomePage />)} />
          <Route path="account" element={safe(<AccountPage />)} />
          <Route path="event-privacy-policy" element={safe(<EventPrivacyPolicyPage />)} />
        </Route>
      </Route>

      <Route path="/cart" element={<NotFound />} />
      <Route path="/checkout" element={<NotFound />} />
      <Route path="/account" element={<NotFound />} />
      <Route path="/order" element={<NotFound />} />
      <Route path="/store" element={<NotFound />} />

      <Route path="/" element={<Navigate to={`/${appConfig.defaults.storeKey}/${appConfig.defaults.eventKey}`} replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
